﻿const DEFAULT_ALLOWED_EVENT_CITIES = ["Antalya"];
const PUBLIC_VISIBILITY_TYPES = ["Public", "VerifiedPublic"];
const DEFAULT_ALLOWED_CREATE_GROUPS = ["Pair", "SmallGroup", "Squad", "EventBulletin", "Platoon"];
const DEFAULT_ALLOWED_VIEW_GROUPS = ["Pair", "SmallGroup", "Squad", "EventBulletin", "Platoon", "LargeGroup", "Organization", "MassEvent", "CityEvent"];
const DEFAULT_ALLOWED_DURATIONS = ["ThirtyMinutes", "OneHour", "OneToTwoHours", "TwoHours", "TwoToThreeHours", "HalfDay", "FullDay"];
const DEFAULT_CONFIRMATION_POLICIES = ["Confirm30MinutesBefore", "Confirm2HoursBefore", "ManualHostConfirm"];
const DEFAULT_CANCELLATION_LOCKS = ["NoCancelCondition", "NoCancel30MinutesBefore", "NoCancelAfterApproval"];
const DEFAULT_LEGAL_KEYS = ["organizer-agreement", "event-permissions", "participation-terms"];
const DEFAULT_PRESET_KEYS = ["balanced", "community", "premium"];
const DEFAULT_AUDIENCE_PREFERENCES = [
  "Students",
  "Professionals",
  "Tourists",
  "Creators",
  "WellnessSeekers",
  "Runners",
  "WaterSports",
  "Gamers",
  "CoffeePeople",
  "CultureLovers",
  "PetOwners",
];
const DEFAULT_PURPOSE_PREFERENCES = ["Entertainment", "Learning", "Networking", "Fitness", "Culture", "Creativity", "CommunityBuilding"];
const DEFAULT_ENERGY_PREFERENCES = ["Calm", "Social", "Active"];
const DEFAULT_TIME_PREFERENCES = ["Morning", "Afternoon", "Evening"];
const DEFAULT_DAY_PREFERENCES = ["Friday", "Saturday", "Sunday"];

function asArray(value, fallback = []) {
  return Array.isArray(value) ? value.filter(Boolean) : fallback;
}

function clamp(value, min, max) {
  const next = Number(value);
  if (!Number.isFinite(next)) return min;
  return Math.min(max, Math.max(min, next));
}

function normalizeKey(value) {
  return String(value || "").trim();
}

function normalizeObjectNumberMap(value = {}, fallback = {}) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : fallback;
  return Object.fromEntries(
    Object.entries(source)
      .map(([key, score]) => [normalizeKey(key), clamp(score, 0, 100)])
      .filter(([key]) => Boolean(key)),
  );
}

function normalizePolicy(value, options, fallback) {
  return options.includes(value) ? value : fallback;
}

function includesWhenDefined(list, value) {
  const safe = asArray(list);
  if (!safe.length) return true;
  return safe.includes(value);
}

function isBlockedByList(list, value) {
  return Boolean(value && asArray(list).includes(value));
}

function countEventsInWindow(events = [], minutes = 120) {
  const now = Date.now();
  const windowMs = Math.max(1, Number(minutes || 120)) * 60 * 1000;
  return events.filter((event) => {
    const createdAt = event?.createdAt instanceof Date ? event.createdAt.getTime() : 0;
    return createdAt && now - createdAt <= windowMs;
  }).length;
}

function parseEventDateTime(dateValue, timeValue) {
  const dateText = String(dateValue || "").trim();
  const timeText = String(timeValue || "").trim();
  const dateMatch = dateText.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const timeMatch = timeText.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!dateMatch || !timeMatch) return null;
  const [, day, month, year] = dateMatch;
  const [, hour, minute] = timeMatch;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0, 0);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function hasPlannedTimeOverlap(events = [], draft = {}, windowMinutes = 180) {
  if (!windowMinutes) return false;
  const target = parseEventDateTime(draft.date, draft.time);
  if (!target) return false;
  const windowMs = Math.max(1, Number(windowMinutes)) * 60 * 1000;
  return events.some((event) => {
    const editable = event.editable || {};
    const planned = parseEventDateTime(editable.date, editable.time);
    if (!planned) return false;
    return Math.abs(planned.getTime() - target.getTime()) < windowMs;
  });
}

export function buildDefaultModerationProfile(seed = {}) {
  return {
    verificationStatus: seed.verificationStatus || "Unverified",
    identityVerificationStatus: seed.identityVerificationStatus || "NotStarted",
    hostVerificationStatus: seed.hostVerificationStatus || "None",
    moderationFlags: asArray(seed.moderationFlags),
    reportCount: clamp(seed.reportCount || 0, 0, 999999),
    validReportCount: clamp(seed.validReportCount || 0, 0, 999999),
    warningCount: clamp(seed.warningCount || 0, 0, 999999),
    strikeCount: clamp(seed.strikeCount || 0, 0, 999999),
    suspensionUntil: seed.suspensionUntil || null,
    shadowLimitReason: seed.shadowLimitReason || null,
    fairUseAcknowledgedAt: seed.fairUseAcknowledgedAt || null,
  };
}

export function buildDefaultEventVisibilityProfile(seed = {}) {
  return {
    allowedCategories: asArray(seed.allowedCategories),
    blockedCategories: asArray(seed.blockedCategories),
    allowedSubCategories: asArray(seed.allowedSubCategories),
    blockedSubCategories: asArray(seed.blockedSubCategories),
    allowedRankLevels: asArray(seed.allowedRankLevels),
    minRequiredTrustScore: clamp(seed.minRequiredTrustScore ?? 0, 0, 100),
    maxEventGroupSizeVisible: clamp(seed.maxEventGroupSizeVisible ?? 100000, 2, 100000),
    allowedGroupSizeKeys: asArray(seed.allowedGroupSizeKeys, DEFAULT_ALLOWED_VIEW_GROUPS),
    allowedEnergyLevels: asArray(seed.allowedEnergyLevels),
    allowedLocationTypes: asArray(seed.allowedLocationTypes),
    indoorOutdoorPreference: asArray(seed.indoorOutdoorPreference),
    allowedTimeOfDay: asArray(seed.allowedTimeOfDay),
    allowedSeasonality: asArray(seed.allowedSeasonality),
    excludedEventIds: asArray(seed.excludedEventIds),
    blockedHostUids: asArray(seed.blockedHostUids),
    priorityEventIds: asArray(seed.priorityEventIds),
    friendHostedBoostEnabled: seed.friendHostedBoostEnabled !== false,
  };
}

export function buildDefaultEventCreateProfile(seed = {}) {
  return {
    canCreateEvent: seed.canCreateEvent !== false,
    allowedCategories: asArray(seed.allowedCategories),
    blockedCategories: asArray(seed.blockedCategories),
    allowedSubCategories: asArray(seed.allowedSubCategories),
    maxActiveEvents: clamp(seed.maxActiveEvents ?? 3, 0, 1000),
    maxDraftEvents: clamp(seed.maxDraftEvents ?? 1, 0, 50),
    dailyCreateLimit: clamp(seed.dailyCreateLimit ?? 3, 0, 1000),
    weeklyCreateLimit: clamp(seed.weeklyCreateLimit ?? 10, 0, 10000),
    hourlyCreateLimit: clamp(seed.hourlyCreateLimit ?? 1, 0, 1000),
    minParticipantsAllowed: clamp(seed.minParticipantsAllowed ?? 2, 2, 100000),
    maxParticipantsAllowed: clamp(seed.maxParticipantsAllowed ?? 20, 2, 100000),
    allowedGroupSizeKeys: asArray(seed.allowedGroupSizeKeys, DEFAULT_ALLOWED_CREATE_GROUPS),
    allowedDurationTypes: asArray(seed.allowedDurationTypes, DEFAULT_ALLOWED_DURATIONS),
    allowedVisibilityTypes: asArray(seed.allowedVisibilityTypes, PUBLIC_VISIBILITY_TYPES),
    canEnableRewards: seed.canEnableRewards !== false,
    canDisableHostApproval: Boolean(seed.canDisableHostApproval),
    allowedConfirmationPolicies: asArray(seed.allowedConfirmationPolicies, DEFAULT_CONFIRMATION_POLICIES),
    allowedCancellationLocks: asArray(seed.allowedCancellationLocks, DEFAULT_CANCELLATION_LOCKS),
    requiresLegalApproval: seed.requiresLegalApproval !== false,
    requiredLegalKeys: asArray(seed.requiredLegalKeys, DEFAULT_LEGAL_KEYS),
    allowedPresetKeys: asArray(seed.allowedPresetKeys, DEFAULT_PRESET_KEYS),
    defaultPresetKey: seed.defaultPresetKey || "balanced",
    requiresModeratorReview: seed.requiresModeratorReview !== false,
    createCooldownMinutes: clamp(seed.createCooldownMinutes ?? 120, 0, 10080),
    noOverlapWindowMinutes: clamp(seed.noOverlapWindowMinutes ?? 180, 0, 10080),
  };
}

export function buildDefaultParticipationProfile(seed = {}) {
  const stats = seed.participationStats || seed.stats || {};
  const eligibility = seed.participationEligibility || seed.eligibility || {};
  const noShowCount = clamp(stats.noShowCount || 0, 0, 999999);
  return {
    participationStats: {
      totalJoined: clamp(stats.totalJoined || 0, 0, 999999999),
      totalHosted: clamp(stats.totalHosted || 0, 0, 999999999),
      completedEvents: clamp(stats.completedEvents || 0, 0, 999999999),
      noShowCount,
      cancelledLateCount: clamp(stats.cancelledLateCount || 0, 0, 999999999),
      confirmationReliability: clamp(stats.confirmationReliability ?? 100, 0, 100),
      earnedExperienceScore: clamp(stats.earnedExperienceScore || 0, 0, 999999999),
      hostContributionScore: clamp(stats.hostContributionScore || 0, 0, 999999999),
      lastMinuteJoinUsedThisWeek: clamp(stats.lastMinuteJoinUsedThisWeek || 0, 0, 999999),
    },
    participationEligibility: {
      blockedEventIds: asArray(eligibility.blockedEventIds),
      blockedHostUids: asArray(eligibility.blockedHostUids),
      minTrustRequiredOverride: eligibility.minTrustRequiredOverride ?? null,
      requiresPhoneForJoin: eligibility.requiresPhoneForJoin !== false,
      requiresIdentityForJoin: Boolean(eligibility.requiresIdentityForJoin || noShowCount >= 2),
      fairUseTermsRequired: Boolean(eligibility.fairUseTermsRequired || noShowCount >= 1),
      suspensionDaysOnThirdNoShow: clamp(eligibility.suspensionDaysOnThirdNoShow ?? 3, 1, 365),
      lastMinuteJoinEnabled: eligibility.lastMinuteJoinEnabled !== false,
      lastMinuteJoinWeeklyLimit: clamp(eligibility.lastMinuteJoinWeeklyLimit ?? 2, 0, 100),
      lastMinuteTierFlex: clamp(eligibility.lastMinuteTierFlex ?? 1, 0, 10),
    },
  };
}

export function buildDefaultPreferenceProfile(seed = {}) {
  const interests = asArray(seed.interests);
  const interestWeights = normalizeObjectNumberMap(
    seed.interestWeights,
    Object.fromEntries(interests.map((interest) => [interest, 60])),
  );

  return {
    interests,
    interestWeights,
    themeStyles: asArray(seed.themeStyles),
    activityTags: asArray(seed.activityTags),
    blockedInterests: asArray(seed.blockedInterests),
    preferredAudience: asArray(seed.preferredAudience, DEFAULT_AUDIENCE_PREFERENCES),
    preferredPurposes: asArray(seed.preferredPurposes, DEFAULT_PURPOSE_PREFERENCES),
    preferredEnergyLevels: asArray(seed.preferredEnergyLevels, DEFAULT_ENERGY_PREFERENCES),
    preferredTimeOfDay: asArray(seed.preferredTimeOfDay, DEFAULT_TIME_PREFERENCES),
    preferredDaysOfWeek: asArray(seed.preferredDaysOfWeek, DEFAULT_DAY_PREFERENCES),
    preferredTimeNotificationEnabled: seed.preferredTimeNotificationEnabled !== false,
    notificationPreferences: {
      preferredTimeMatches: seed.notificationPreferences?.preferredTimeMatches !== false,
      friendHostedEvents: seed.notificationPreferences?.friendHostedEvents !== false,
      lastMinuteChanceEvents: seed.notificationPreferences?.lastMinuteChanceEvents !== false,
      socialUpdates: seed.notificationPreferences?.socialUpdates !== false,
    },
    contentLanguagePreferences: ["tr"],
    aiRecommendationProfile: {
      visibility: "SystemOnly",
      lastComputedAt: seed.aiRecommendationProfile?.lastComputedAt || null,
      topSignals: asArray(seed.aiRecommendationProfile?.topSignals),
      excludedSignals: asArray(seed.aiRecommendationProfile?.excludedSignals),
    },
  };
}

export function buildDefaultSocialProfile(seed = {}) {
  return {
    friendCount: clamp(seed.friendCount || 0, 0, 999999999),
    friendUids: asArray(seed.friendUids),
    pendingIncomingFriendCount: clamp(seed.pendingIncomingFriendCount || 0, 0, 999999),
    pendingOutgoingFriendCount: clamp(seed.pendingOutgoingFriendCount || 0, 0, 999999),
    blockedUserUids: asArray(seed.blockedUserUids),
    mutedUserUids: asArray(seed.mutedUserUids),
    closeFriendUids: asArray(seed.closeFriendUids),
    socialGraphStrength: normalizeObjectNumberMap(seed.socialGraphStrength),
    relationTags: asArray(seed.relationTags),
    friendAddMode: normalizePolicy(seed.friendAddMode, ["Open", "ApprovalRequired"], "ApprovalRequired"),
    friendshipRequestPolicy: normalizePolicy(seed.friendshipRequestPolicy, ["Everyone", "VerifiedOnly"], "Everyone"),
    friendRequestExpiresHours: clamp(seed.friendRequestExpiresHours || 48, 1, 720),
    profileMessagePolicy: normalizePolicy(seed.profileMessagePolicy, ["Everyone", "FriendsOnly", "EventParticipantsOnly", "SquadOnly", "Nobody"], "FriendsOnly"),
    squadMemberships: asArray(seed.squadMemberships),
    platoonMemberships: asArray(seed.platoonMemberships),
    activeSquadId: seed.activeSquadId || null,
    activePlatoonId: seed.activePlatoonId || null,
  };
}

export function buildDefaultPrivatePolicy(seed = {}) {
  return {
    trustProfile: {
      trustScore: clamp(seed?.trustProfile?.trustScore ?? seed?.trustProfile?.trustPercent ?? 50, 0, 100),
      trustPercent: clamp(seed?.trustProfile?.trustPercent ?? seed?.trustProfile?.trustScore ?? 50, 0, 100),
      visibility: "AdminOnly",
      noShowPenaltyCount: clamp(seed?.trustProfile?.noShowPenaltyCount || 0, 0, 999999),
      inactivityPenaltyCount: clamp(seed?.trustProfile?.inactivityPenaltyCount || 0, 0, 999999),
    },
    moderationProfile: buildDefaultModerationProfile(seed.moderationProfile),
    eventVisibilityProfile: buildDefaultEventVisibilityProfile(seed.eventVisibilityProfile),
    eventCreateProfile: buildDefaultEventCreateProfile(seed.eventCreateProfile),
    preferenceProfile: buildDefaultPreferenceProfile(seed.preferenceProfile),
    socialProfile: buildDefaultSocialProfile(seed.socialProfile),
    ...buildDefaultParticipationProfile(seed),
  };
}

export function getTrustScore(account = {}) {
  return clamp(account?.trustProfile?.trustScore ?? account?.trustProfile?.trustPercent ?? 50, 0, 100);
}

export function isSuspended(account = {}, now = new Date()) {
  const raw = account?.moderationProfile?.suspensionUntil || account?.suspensionUntil;
  const date = raw?.toDate?.() || (raw ? new Date(raw) : null);
  return Boolean(date && Number.isFinite(date.getTime()) && date.getTime() > now.getTime());
}

export function filterVisibleEventsForAccount(events = [], account = {}) {
  const profile = buildDefaultEventVisibilityProfile(account?.eventVisibilityProfile);
  const preference = buildDefaultPreferenceProfile(account?.preferenceProfile);
  const trustScore = getTrustScore(account);
  return events
    .filter((event) => {
      const editable = event.editable || {};
      if (profile.excludedEventIds.includes(event.id)) return false;
      if (profile.blockedHostUids.includes(event.host?.uid)) return false;
      const eventText = [event.title, event.category, event.subCategory, event.locationType, ...(event.tags || [])].join(" ").toLocaleLowerCase("tr-TR");
      if (preference.blockedInterests.some((interest) => eventText.includes(String(interest).toLocaleLowerCase("tr-TR")))) return false;
      if (isBlockedByList(profile.blockedCategories, event.category)) return false;
      if (isBlockedByList(profile.blockedSubCategories, event.subCategory)) return false;
      if (!includesWhenDefined(profile.allowedCategories, event.category)) return false;
      if (!includesWhenDefined(profile.allowedSubCategories, event.subCategory)) return false;
      if (!includesWhenDefined(profile.allowedRankLevels, event.rankLevel)) return false;
      if (!includesWhenDefined(profile.allowedGroupSizeKeys, event.groupSize)) return false;
      if (!includesWhenDefined(profile.allowedEnergyLevels, event.energy)) return false;
      if (!includesWhenDefined(profile.allowedLocationTypes, event.locationType)) return false;
      if (!includesWhenDefined(profile.indoorOutdoorPreference, event.indoorOutdoor)) return false;
      if (profile.allowedTimeOfDay.length && !asArray(event.timeOfDay).some((key) => profile.allowedTimeOfDay.includes(key))) return false;
      if (profile.allowedSeasonality.length && !asArray(event.seasonality).some((key) => profile.allowedSeasonality.includes(key))) return false;
      if (Number(editable.maxParticipants || 0) > profile.maxEventGroupSizeVisible) return false;
      const requiredTrust = clamp(event.minRequiredTrustScore ?? editable.minRequiredTrustScore ?? 0, 0, 100);
      if (trustScore < Math.max(profile.minRequiredTrustScore, requiredTrust)) return false;
      return true;
    })
    .sort((a, b) => {
      const aPriority = profile.priorityEventIds.includes(a.id) ? 1 : 0;
      const bPriority = profile.priorityEventIds.includes(b.id) ? 1 : 0;
      if (aPriority !== bPriority) return bPriority - aPriority;
      const scoreEvent = (event) => {
        const text = [event.title, event.category, event.subCategory, ...(event.tags || [])].join(" ").toLocaleLowerCase("tr-TR");
        return Object.entries(preference.interestWeights).reduce((score, [interest, weight]) => (
          text.includes(String(interest).toLocaleLowerCase("tr-TR")) ? score + Number(weight || 0) : score
        ), 0);
      };
      return scoreEvent(b) - scoreEvent(a);
    });
}

export function filterVisiblePeopleForAccount(people = [], account = {}) {
  const social = buildDefaultSocialProfile(account?.socialProfile);
  const preference = buildDefaultPreferenceProfile(account?.preferenceProfile);
  return people
    .filter((person) => {
      const uid = person.uid || person.id;
      if (social.blockedUserUids.includes(uid)) return false;
      const text = [person.name, person.displayName, person.title, person.profileTitle, ...(person.interests || [])].join(" ").toLocaleLowerCase("tr-TR");
      return !preference.blockedInterests.some((interest) => text.includes(String(interest).toLocaleLowerCase("tr-TR")));
    })
    .sort((a, b) => {
      const scorePerson = (person) => {
        const uid = person.uid || person.id;
        const interests = asArray(person.interests);
        const interestScore = interests.reduce((score, interest) => score + Number(preference.interestWeights[interest] || 0), 0);
        const graphScore = Number(social.socialGraphStrength[uid] || 0);
        const mutedPenalty = social.mutedUserUids.includes(uid) ? -500 : 0;
        const closeBoost = social.closeFriendUids.includes(uid) ? 250 : 0;
        return interestScore + graphScore + closeBoost + mutedPenalty;
      };
      return scorePerson(b) - scorePerson(a);
    });
}

export function canSendFriendRequest({ fromAccount = {}, toProfile = {}, toAccount = null, isFromVerified = false } = {}) {
  const fromSocial = buildDefaultSocialProfile(fromAccount?.socialProfile);
  const targetSocial = buildDefaultSocialProfile(toAccount?.socialProfile || toProfile?.socialProfile);
  const targetUid = toProfile.uid || toProfile.id;
  if (!targetUid) return { allowed: false, message: "Hedef kullanıcı bulunamadı." };
  if (fromSocial.blockedUserUids.includes(targetUid)) return { allowed: false, message: "Engellediğin kullanıcıya arkadaşlık isteği gönderemezsin." };
  if (targetSocial.blockedUserUids.includes(fromAccount.uid)) return { allowed: false, message: "Bu kullanıcı arkadaşlık isteği kabul etmiyor." };
  if (targetSocial.friendshipRequestPolicy === "VerifiedOnly" && !isFromVerified) return { allowed: false, message: "Bu kullanıcı yalnızca doğrulanmış hesaplardan arkadaşlık isteği alıyor." };
  const requestStatus = targetSocial.friendAddMode === "Open" ? "accepted" : "pending";
  return {
    allowed: true,
    requestStatus,
    requiresApproval: requestStatus === "pending",
    message: requestStatus === "accepted" ? "Arkadaş olarak eklendi." : "Arkadaşlık isteği gönderilebilir.",
  };
}

export function validateEventCreatePolicy({ account = {}, draft = {}, selectedIdea = null, existingEvents = [], legalAccepted = true } = {}) {
  const profile = buildDefaultEventCreateProfile(account?.eventCreateProfile);
  const moderation = buildDefaultModerationProfile(account?.moderationProfile);
  const trustScore = getTrustScore(account);
  const minParticipants = Number(draft.minParticipants || 0);
  const maxParticipants = Number(draft.maxParticipants || 0);
  const category = selectedIdea?.category || "Custom";
  const subCategory = selectedIdea?.subCategory || "Custom";
  const groupSize = draft?.tunable?.groupSize || selectedIdea?.groupSize || "Squad";
  const durationType = draft.durationType || selectedIdea?.durationType || "TwoHours";
  const visibility = normalizeKey(draft.visibility || "Public");
  const activeEvents = existingEvents.filter((event) => event.status === "active" || event.status === "draft");

  if (isSuspended(account)) return { allowed: false, field: "auth", message: "Hesap geçici olarak askıda olduğu için etkinlik oluşturamazsın." };
  if (moderation.strikeCount >= 3) return { allowed: false, field: "auth", message: "Moderasyon strike limiti nedeniyle etkinlik oluşturma kapalı." };
  if (!profile.canCreateEvent) return { allowed: false, field: "auth", message: "Bu hesap için etkinlik oluşturma yetkisi kapalı." };
  if (profile.requiresLegalApproval && !legalAccepted) return { allowed: false, field: "legal", message: "Şartların kabul edilmesi gerekmektedir." };
  if (activeEvents.length >= profile.maxActiveEvents) return { allowed: false, field: "firebase", message: `Aynı anda en fazla ${profile.maxActiveEvents} aktif etkinlik oluşturabilirsin.` };
  if (profile.hourlyCreateLimit > 0 && countEventsInWindow(existingEvents, 60) >= profile.hourlyCreateLimit) return { allowed: false, field: "firebase", message: `Saatlik etkinlik oluşturma limitin doldu. Limit: ${profile.hourlyCreateLimit}.` };
  if (profile.dailyCreateLimit > 0 && countEventsInWindow(existingEvents, 1440) >= profile.dailyCreateLimit) return { allowed: false, field: "firebase", message: `Günlük etkinlik oluşturma limitin doldu. Limit: ${profile.dailyCreateLimit}.` };
  if (profile.weeklyCreateLimit > 0 && countEventsInWindow(existingEvents, 10080) >= profile.weeklyCreateLimit) return { allowed: false, field: "firebase", message: `Haftalık etkinlik oluşturma limitin doldu. Limit: ${profile.weeklyCreateLimit}.` };
  if (profile.createCooldownMinutes > 0 && countEventsInWindow(existingEvents, profile.createCooldownMinutes) > 0) return { allowed: false, field: "firebase", message: `Yeni etkinlik oluşturmak için ${profile.createCooldownMinutes} dakika cooldown beklenmeli.` };
  if (profile.noOverlapWindowMinutes > 0 && hasPlannedTimeOverlap(activeEvents, draft, profile.noOverlapWindowMinutes)) return { allowed: false, field: "time", message: `Bu saat aralığında zaten aktif bir etkinliğin var. Çakışma penceresi: ${profile.noOverlapWindowMinutes} dakika.` };
  if (isBlockedByList(profile.blockedCategories, category)) return { allowed: false, field: "category", message: `${category} kategorisinde etkinlik oluşturma yetkin yok.` };
  if (!includesWhenDefined(profile.allowedCategories, category)) return { allowed: false, field: "category", message: `${category} kategorisi bu hesap için açık değil.` };
  if (!includesWhenDefined(profile.allowedSubCategories, subCategory)) return { allowed: false, field: "category", message: `${subCategory} alt kategorisi bu hesap için açık değil.` };
  if (!profile.allowedGroupSizeKeys.includes(groupSize)) return { allowed: false, field: "participants", message: `${groupSize} grup tipi bu hesap için açık değil.` };
  if (!profile.allowedDurationTypes.includes(durationType)) return { allowed: false, field: "duration", message: `${durationType} süresi bu hesap için açık değil.` };
  if (!profile.allowedVisibilityTypes.includes(visibility)) return { allowed: false, field: "visibility", message: `${visibility} görünürlüğü bu hesap için açık değil.` };
  if (minParticipants < profile.minParticipantsAllowed) return { allowed: false, field: "participants", message: `Minimum kişi sayısı en az ${profile.minParticipantsAllowed} olmalı.` };
  if (maxParticipants > profile.maxParticipantsAllowed) return { allowed: false, field: "participants", message: `Bu hesap en fazla ${profile.maxParticipantsAllowed} kişilik etkinlik oluşturabilir.` };
  if (draft.rewardsEnabled && !profile.canEnableRewards) return { allowed: false, field: "rewards", message: "Bu hesap ödüllü etkinlik oluşturamaz." };
  if (!draft.approvalRequired && !profile.canDisableHostApproval && trustScore < 70) return { allowed: false, field: "approval", message: "Bu hesapta host onayı kapatılamaz; güven profili moderasyon tarafından izlenir." };
  if (!profile.allowedConfirmationPolicies.includes(draft.confirmationPolicy)) return { allowed: false, field: "confirmation", message: "Seçilen onay süresi bu hesap için açık değil." };
  if (!profile.allowedCancellationLocks.includes(draft.cancellationLock)) return { allowed: false, field: "cancellation", message: "Seçilen iptal kilidi bu hesap için açık değil." };
  if (!profile.allowedPresetKeys.includes(draft.presetKey)) return { allowed: false, field: "preset", message: "Seçilen preset bu hesap için açık değil." };
  return { allowed: true, field: "", message: "Etkinlik oluşturma policy kontrolü geçti.", profile };
}

export function evaluateJoinEligibility({ account = {}, event = {}, isEmailVerified = false, isPhoneVerified = false } = {}) {
  const participation = buildDefaultParticipationProfile(account);
  const moderation = buildDefaultModerationProfile(account?.moderationProfile);
  const eligibility = participation.participationEligibility;
  const stats = participation.participationStats;

  if (isSuspended(account)) return { allowed: false, type: "suspension", tone: "amber", title: "Hesap Askıda", message: "Geçici uzaklaştırma süren bitmeden etkinliğe katılamazsın.", action: "Ayarları Aç", path: "/ayarlar" };
  if (moderation.strikeCount >= 3 || stats.noShowCount >= 3) return { allowed: false, type: "fair-use-ban", tone: "amber", title: "Adil Kullanım Kısıtı", message: "Üçüncü katılmama ihlali sonrası etkinlik katılımı geçici olarak kapatılır.", action: "Topluluk Kurallarını Gör", path: "/topluluk-ilkeleri" };
  if (eligibility.blockedEventIds.includes(event.id)) return { allowed: false, type: "blocked-event", tone: "amber", title: "Etkinlik Kısıtlı", message: "Bu etkinlik için katılım iznin bulunmuyor.", action: "Keşfet'e Dön", path: "/kesfet" };
  if (eligibility.blockedHostUids.includes(event.host?.uid)) return { allowed: false, type: "blocked-host", tone: "amber", title: "Host Kısıtı", message: "Bu hostun etkinliklerine katılımın sistem tarafından kapatılmış.", action: "Keşfet'e Dön", path: "/kesfet" };
  if (!isEmailVerified) return { allowed: false, type: "email", tone: "purple", title: "E-Posta Doğrulaması Gerekli", message: "Etkinliğe katılmadan önce e-posta adresini doğrulamalısın. Onay tamamlanmadan katılım isteği alınmaz.", action: "E-Posta Doğrulamasına Git", path: "/email-dogrulama" };
  if (eligibility.requiresPhoneForJoin && !isPhoneVerified) return { allowed: false, type: "phone", tone: "green", title: "Telefon Doğrulaması Gerekli", message: "DenkUP etkinliklerine katılmak için SMS doğrulaması zorunludur. Bu bilgi public görünmez; Verified rozeti için de kullanılır.", action: "Telefon Doğrulamasına Git", path: "/telefon-dogrulama" };
  if (eligibility.requiresIdentityForJoin && moderation.identityVerificationStatus !== "Approved") return { allowed: false, type: "identity", tone: "purple", title: "Kimlik Onayı Gerekli", message: "Tekrarlayan katılmama veya hassas etkinlik kuralları nedeniyle kimlik onayı tamamlanmadan katılım açılamaz.", action: "Doğrulama Ayarlarına Git", path: "/ayarlar" };
  return { allowed: true, type: "ok" };
}
